import { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Col,
  Row,
  Container,
  Button,
  Card,
  CardBody,
  ModalHeader,
  Modal,
  ModalBody,
  Label,
  ModalFooter,
  CustomInput,
} from 'reactstrap';
import PageHeader from '~/components/Common/PageHeader';
import ConfirmDeleteModal from '~/components/Modals/ConfirmDeleteModal';
import Loading from '~/components/Loading';
import Pagination from '~/components/Pagination';
import SearchBox from '~/components/SearchBox';
import { resetSearch, setSearch } from '~/store/modules/search/actions';
import { toast } from 'react-toastify';
import { fileTypes } from '~/util/fileTypes';
import DatePicker from '~/components/DatePicker';
import ReactSelect from 'react-select';

import history from '~/services/history';
import useRoles from '~/hooks/useRoles';
import { formatPrice } from '~/util/format';
import { getRole } from '~/util/getRole';
import api from '~/services/api';

const SplitReceiverCard = ({
  receiver,
  setReceiverToDelete,
  setShowDeleteModal,
}) => {
  const [showExportModal, setShowExportModal] = useState(false);
  const [optionsReport, setOptionsReport] = useState({
    export: true,
    file_type: 'xlsx',
    // dateFinish: format(new Date(), 'yyyy-MM-dd', { locale: ptBR }),
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [automaticReportEmails, setAutomaticReportEmails] = useState(
    receiver.automatic_report_emails_enabled === true
  );
  const [updatingReportEmails, setUpdatingReportEmails] = useState(false);

  const [receiverStores, setReceiverStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  const serviceType = () => {
    if (receiver.service_type === 'delivery') return 'Entrega';

    return 'Pagamento';
  };

  const personType = () => {
    if (receiver.type === 'company') return 'Jurídica';

    return 'Física';
  };

  const transferInterval = () => {
    if (receiver.transfer_settings.transfer_interval === 'Daily')
      return 'Diário';

    if (receiver.transfer_settings.transfer_interval === 'Weekly')
      return 'Semanal';

    return 'Mensal';
  };

  const getWeekDayLabel = day => {
    const weekDays = {
      1: 'Segunda-feira',
      2: 'Terça-feira',
      3: 'Quarta-feira',
      4: 'Quinta-feira',
      5: 'Sexta-feira',
    };
    return weekDays[day] || day;
  };

  const getPaymentTypeLabel = type => {
    if (type === 'pix') return 'Pix';
    if (type === 'credit_card') return 'Cartão de Crédito';
    return type;
  };

  const { isSupport } = useRoles();

  const roles = useSelector(({ user }) => user.profile.roles);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
        'financier',
      ]),
    [roles]
  );

  useEffect(() => {
    if (!showExportModal || !role) return undefined;

    let cancelled = false;

    const fetchReceiverStores = async () => {
      setLoadingStores(true);

      try {
        const { data } = await api.get(
          `/${role}/recipients/${receiver.id}/stores`
        );

        if (cancelled) return;

        const unique = {};

        (data.stores || []).forEach(item => {
          if (item.store && !unique[item.store.id]) {
            unique[item.store.id] = {
              value: item.store.id,
              label: item.store.fantasy_name || item.store.name,
            };
          }
        });

        setReceiverStores(Object.values(unique));
      } catch (err) {
        if (!cancelled) {
          setReceiverStores([]);
          toast.error('Não foi possível carregar as lojas do recebedor.');
        }
      } finally {
        if (!cancelled) setLoadingStores(false);
      }
    };

    fetchReceiverStores();

    return () => {
      cancelled = true;
    };
  }, [showExportModal, role, receiver.id]);

  const handleGenerateReport = async () => {
    try {
      toast.info(
        'Estamos gerando seu relatório, e ele será enviado para o seu e-mail.'
      );

      await api.get(`/${role}/recipients/${receiver.id}/conciliation/export`, {
        params: optionsReport,
        responseType: 'blob',
      });

      setStartDate(null);
      setEndDate(null);

      setOptionsReport({
        export: true,
        file_type: 'xlsx',
        // dateFinish: format(new Date(), 'yyyy-MM-dd', { locale: ptBR }),
      });

      setShowExportModal(false);
      toast.success('Relatório gerado, já deve constar no seu e-mail.');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else {
        toast.error('Erro ao gerar relatório');
      }
    }
  };

  const handleToggleReportEmails = async () => {
    const nextValue = !automaticReportEmails;

    setUpdatingReportEmails(true);
    setAutomaticReportEmails(nextValue);

    try {
      await api.patch(
        `/${role}/recipients/${receiver.id}/automatic-report-emails`,
        { automatic_report_emails_enabled: nextValue }
      );

      toast.success(
        nextValue
          ? 'Envio automático do relatório ativado.'
          : 'Envio automático do relatório desativado.'
      );
    } catch (err) {
      setAutomaticReportEmails(!nextValue);

      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else {
        toast.error('Erro ao atualizar o envio automático do relatório.');
      }
    } finally {
      setUpdatingReportEmails(false);
    }
  };

  return (
    <Card className="card new-layout mt-4">
      <CardBody className="card-body new-layout px-3 py-3">
        <Row className="w-100 mx-0">
          <Col className="d-flex align-items-start justify-content-start mx-0 font-size-18 font-weight-bold">
            {receiver.description}
          </Col>
        </Row>
        <Row className="w-100 mx-0">
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-3">
            <b>Nome</b>
            <p className="text-muted">{receiver.name}</p>
          </Col>
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
            <b>Telefone</b>
            <p className="text-muted">{receiver.telephone}</p>
          </Col>
          <Col className="d-flex flex-column mx-0 font-size-14 mt-3">
            <b>Loja</b>
            <p className="text-muted">
              {receiver.store?.fantasy_name || 'Loja não vinculada / removida'}
            </p>
          </Col>
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
            <b>Tipo de Pessoa</b>
            <p className="text-muted">{personType()}</p>
          </Col>
        </Row>
        <Row className="w-100 mx-0">
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
            <b>Tipo de Serviço</b>
            <p className="text-muted">{serviceType()}</p>
          </Col>
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
            <b>Intervalo de Transferência</b>
            <p className="text-muted">{transferInterval()}</p>
          </Col>
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
            <b>Dia da Transferência</b>
            <p className="text-muted">
              {receiver.transfer_settings.transfer_interval === 'Weekly'
                ? getWeekDayLabel(receiver.transfer_settings.transfer_day)
                : receiver.transfer_settings.transfer_day}
            </p>
          </Col>
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
            <b>Responsável por Chargeback e Taxas</b>
            <p className="text-muted">
              {receiver.split_rules?.liable ? 'Sim' : 'Não'}
            </p>
          </Col>
        </Row>
        <Row className="w-100 mx-0">
          <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
            <b>Relatório Automático por E-mail</b>
            <p
              className={automaticReportEmails ? 'text-success' : 'text-muted'}
            >
              {automaticReportEmails ? 'Ativo' : 'Inativo'}
            </p>
          </Col>
          {serviceType() === 'Pagamento' && (
            <>
              {receiver.split_rules && (
                <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
                  <b>Participação no Repasse</b>
                  <p className="text-muted">
                    {receiver.split_rules.type === 'percentage'
                      ? `${receiver.split_rules.amount}%`
                      : formatPrice(receiver.split_rules.amount)}
                  </p>
                </Col>
              )}
              {receiver.dynamic_split_rules &&
                receiver.dynamic_split_rules.length > 0 && (
                  <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 font-size-14 mt-1">
                    <b>Repasse por Tipo de Pagamento</b>
                    <p className="text-muted">
                      {receiver.dynamic_split_rules
                        .map(
                          rule =>
                            `${getPaymentTypeLabel(
                              rule.payment_type
                            )}: ${formatPrice(rule.amount)}`
                        )
                        .join(' | ')}
                    </p>
                  </Col>
                )}
            </>
          )}
          <Col />
          <Col />
        </Row>
        <Row className="w-100 mx-0 align-items-center justify-content-between">
          {receiver.balance && typeof receiver.balance !== 'string' ? (
            <Col
              className="p-2"
              style={{
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                backgroundColor: '#FAFAFA',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <span
                style={{ color: '#27AE60', fontWeight: 500, marginRight: 12 }}
              >
                A receber:{' '}
                {formatPrice(Number(receiver.balance.waiting_funds_amount))}
              </span>
              <span style={{ margin: '0 10px', color: '#C4C4C4' }}>|</span>
              <span
                style={{ color: '#2F80ED', fontWeight: 500, marginRight: 12 }}
              >
                Disponível:{' '}
                {formatPrice(Number(receiver.balance.available_amount))}
              </span>
              <span style={{ margin: '0 10px', color: '#C4C4C4' }}>|</span>
              <span style={{ color: '#9B51E0', fontWeight: 500 }}>
                Transferido:{' '}
                {formatPrice(Number(receiver.balance.transferred_amount))}
              </span>
            </Col>
          ) : (
            <Col
              className="p-2"
              style={{
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                backgroundColor: '#FAFAFA',
                fontWeight: 500,
                color: '#EB5757',
              }}
            >
              {receiver.balance}
            </Col>
          )}
          <Col className="d-flex justify-content-end flex-wrap mt-2 mt-lg-0">
            {/*
            <Button
              className="new-layout-btn new-layout-btn-transparent main d-flex align-items-center justify-content-center mb-2 mb-lg-0"
              color="primary"
              // eslint-disable-next-line no-undef
              onClick={() => setShowExportModal(true)}
            >
              Conciliação
            </Button>
            */}

            <Button
              className="new-layout-btn new-layout-btn-transparent main d-flex align-items-center justify-content-center mb-2 ml-lg-3"
              color="primary"
              onClick={() =>
                history.push(`/recebedores-split/${receiver.id}/movimentacoes`)
              }
            >
              Movimentações
            </Button>
            {isSupport && (
              <>
                <Button
                  className="new-layout-btn new-layout-btn-transparent main d-flex align-items-center justify-content-center mb-2 ml-lg-3"
                  color="primary"
                  onClick={() =>
                    history.push(`/recebedores-split/${receiver.id}/editar`)
                  }
                >
                  Editar
                </Button>

                <Button
                  className="new-layout-btn new-layout-btn-transparent main d-flex align-items-center justify-content-center mb-2 ml-lg-3"
                  color="primary"
                  onClick={() => {
                    setReceiverToDelete(receiver);
                    setShowDeleteModal(true);
                  }}
                >
                  Excluir
                </Button>
              </>
            )}
          </Col>
        </Row>
        <Modal isOpen={showExportModal}>
          <ModalHeader
            toggle={() => {
              setShowExportModal(false);
              setOptionsReport(state => ({ ...state, storeId: undefined }));
            }}
          >
            Conciliação: {receiver.name}
          </ModalHeader>
          <ModalBody>
            <Row className="w-100 mx-0 my-2">
              <Col className="px-0">
                <Label>Deseja filtrar por uma loja específica?</Label>
                <ReactSelect
                  name="store-manager-report"
                  id="store-manager-report"
                  options={receiverStores}
                  classNamePrefix="select"
                  placeholder="Todas as lojas"
                  isClearable
                  isLoading={loadingStores}
                  isDisabled={loadingStores}
                  value={
                    receiverStores.find(
                      s => s.value === optionsReport.storeId
                    ) || null
                  }
                  onChange={option => {
                    setOptionsReport({
                      ...optionsReport,
                      storeId: option?.value,
                    });
                  }}
                  noOptionsMessage={() =>
                    'Este recebedor não está vinculado a nenhuma loja'
                  }
                />
              </Col>
            </Row>
            <Row className="w-100 mx-0 my-2">
              <Col className="px-0">
                <Label>
                  Deseja filtrar por um intervalo de datas específico?
                </Label>
                <Row className="w-100 mx-0 mb-2 mb-lg-2 d-flex flex-row align-items-center">
                  <Col xs="6" className="pl-0 pr-2 mb-2 mb-lg-0">
                    <DatePicker
                      value={startDate}
                      onChange={selectedDate => {
                        const date = format(selectedDate, 'yyyy-MM-dd', {
                          locale: ptBR,
                        });
                        setOptionsReport({
                          ...optionsReport,
                          dateStart: date,
                        });
                        setStartDate(selectedDate);
                      }}
                      mask="__/__/___"
                      placeholder="Dia Inicial"
                    />
                  </Col>
                  <Col xs="6" className="pl-0 pr-2 mb-2 mb-lg-0">
                    <DatePicker
                      value={endDate}
                      onChange={selectedDate => {
                        const date = format(selectedDate, 'yyyy-MM-dd', {
                          locale: ptBR,
                        });
                        setOptionsReport({
                          ...optionsReport,
                          dateFinish: date,
                        });
                        setEndDate(selectedDate);
                      }}
                      mask="__/__/___"
                      placeholder="Dia Final"
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="w-100 mx-0 mb-4 mt-2">
              <Col className="px-0">
                <Label>Qual formato de arquivo que deseja no relatório?</Label>
                <ReactSelect
                  name="filetype-financier-report"
                  id="filetype-financier-report"
                  options={fileTypes}
                  classNamePrefix="select"
                  placeholder="Formato de arquivo"
                  isClearable
                  value={fileTypes.find(
                    p => p?.value === optionsReport?.file_type
                  )}
                  onChange={option => {
                    // eslint-disable-next-line no-shadow
                    setOptionsReport(state => ({
                      ...state,
                      file_type: option?.value,
                    }));
                  }}
                />
              </Col>
            </Row>
            <hr className="my-3" />
            <Row className="w-100 mx-0 mb-2">
              <Col
                className="p-3"
                style={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  backgroundColor: '#FAFAFA',
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span className="font-size-14 font-weight-bold text-dark">
                    Envio automático diário
                  </span>
                  <CustomInput
                    type="switch"
                    id={`automatic-report-emails-${receiver.id}`}
                    name={`automatic-report-emails-${receiver.id}`}
                    className="text-primary z-index-0"
                    label={automaticReportEmails ? 'Ativo' : 'Inativo'}
                    checked={automaticReportEmails}
                    disabled={updatingReportEmails}
                    onChange={handleToggleReportEmails}
                  />
                </div>
                <p className="text-muted font-size-12 mb-0 mt-2">
                  {automaticReportEmails
                    ? `O relatório será enviado todos os dias às 02h para ${
                        receiver.email || 'o e-mail cadastrado no recebedor'
                      }.`
                    : 'O recebimento automático do relatório de conciliação está desativado. Ative para receber todos os dias.'}
                </p>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              size="lg"
              color="primary"
              className="new-layout-btn main"
              onClick={handleGenerateReport}
              // disabled={!selectedExport}
            >
              Exportar Relatório
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

const GeneralConciliationModal = ({ isOpen, onClose, role }) => {
  const [optionsReport, setOptionsReport] = useState({
    export: true,
    file_type: 'xlsx',
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [receiverOptions, setReceiverOptions] = useState([]);
  const [loadingReceivers, setLoadingReceivers] = useState(false);

  const [receiverStores, setReceiverStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!isOpen || !role) return undefined;

    let cancelled = false;

    const fetchAllReceivers = async () => {
      setLoadingReceivers(true);

      try {
        const { data } = await api.get(`/${role}/recipients`, {
          params: { per_page: 999 },
        });

        if (cancelled) return;

        setReceiverOptions(
          (data.recipients || []).map(recipient => ({
            value: recipient.id,
            label: recipient.description
              ? `${recipient.name} — ${recipient.description}`
              : recipient.name,
          }))
        );
      } catch (err) {
        if (!cancelled) {
          setReceiverOptions([]);
          toast.error('Não foi possível carregar os recebedores.');
        }
      } finally {
        if (!cancelled) setLoadingReceivers(false);
      }
    };

    fetchAllReceivers();

    return () => {
      cancelled = true;
    };
  }, [isOpen, role]);

  useEffect(() => {
    if (!isOpen || !role) {
      setReceiverStores([]);
      return undefined;
    }

    let cancelled = false;

    const fetchStores = async () => {
      setLoadingStores(true);

      try {
        let stores = [];

        if (optionsReport.recipientId) {
          const { data } = await api.get(
            `/${role}/recipients/${optionsReport.recipientId}/stores`
          );

          if (cancelled) return;

          const unique = {};

          (data.stores || []).forEach(item => {
            if (item.store && !unique[item.store.id]) {
              unique[item.store.id] = {
                value: item.store.id,
                label: item.store.fantasy_name || item.store.name,
              };
            }
          });

          stores = Object.values(unique);
        } else {
          const { data } = await api.get(`/${role}/stores/split-enabled`);

          if (cancelled) return;

          stores = (data.stores || []).map(store => ({
            value: store.id,
            label: store.fantasy_name || store.name,
          }));
        }

        setReceiverStores(stores);

        setOptionsReport(state => {
          if (state.storeId && !stores.some(s => s.value === state.storeId)) {
            return { ...state, storeId: undefined };
          }
          return state;
        });
      } catch (err) {
        if (!cancelled) {
          setReceiverStores([]);
          toast.error('Não foi possível carregar as lojas.');
        }
      } finally {
        if (!cancelled) setLoadingStores(false);
      }
    };

    fetchStores();

    return () => {
      cancelled = true;
    };
  }, [isOpen, role, optionsReport.recipientId]);

  const resetAndClose = () => {
    setStartDate(null);
    setEndDate(null);
    setOptionsReport({
      export: true,
      file_type: 'xlsx',
    });
    setReceiverStores([]);
    onClose();
  };

  const handleGenerateReport = async () => {
    try {
      setExporting(true);

      toast.info(
        'Estamos gerando seu relatório, e ele será enviado para o seu e-mail.'
      );

      await api.get(
        `/administrator/recipients/conciliation/export`,
        optionsReport,
        {
          responseType: 'blob',
        }
      );

      resetAndClose();
      toast.success('Relatório gerado, já deve constar no seu e-mail.');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else {
        toast.error('Erro ao gerar relatório');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen}>
      <ModalHeader toggle={resetAndClose}>
        Relatório Geral de Conciliação
      </ModalHeader>
      <ModalBody>
        <Row className="w-100 mx-0 my-2">
          <Col className="px-0">
            <Label>Deseja filtrar por um recebedor específico?</Label>
            <ReactSelect
              name="recipient-general-report"
              id="recipient-general-report"
              options={receiverOptions}
              classNamePrefix="select"
              placeholder="Todos os recebedores"
              isClearable
              isLoading={loadingReceivers}
              isDisabled={loadingReceivers}
              value={
                receiverOptions.find(
                  r => r.value === optionsReport.recipientId
                ) || null
              }
              onChange={option => {
                setOptionsReport(state => ({
                  ...state,
                  recipientId: option?.value,
                }));
              }}
              noOptionsMessage={() => 'Nenhum recebedor encontrado'}
            />
          </Col>
        </Row>
        <Row className="w-100 mx-0 my-2">
          <Col className="px-0">
            <Label>Deseja filtrar por uma loja específica?</Label>
            <ReactSelect
              name="store-general-report"
              id="store-general-report"
              options={receiverStores}
              classNamePrefix="select"
              placeholder="Todas as lojas"
              isClearable
              isLoading={loadingStores}
              isDisabled={loadingStores}
              value={
                receiverStores.find(s => s.value === optionsReport.storeId) ||
                null
              }
              onChange={option => {
                setOptionsReport(state => ({
                  ...state,
                  storeId: option?.value,
                }));
              }}
              noOptionsMessage={() =>
                optionsReport.recipientId
                  ? 'Este recebedor não está vinculado a nenhuma loja'
                  : 'Nenhuma loja encontrada'
              }
            />
          </Col>
        </Row>
        <Row className="w-100 mx-0 my-2">
          <Col className="px-0">
            <Label>Deseja filtrar por um intervalo de datas específico?</Label>
            <Row className="w-100 mx-0 mb-2 mb-lg-2 d-flex flex-row align-items-center">
              <Col xs="6" className="pl-0 pr-2 mb-2 mb-lg-0">
                <DatePicker
                  value={startDate}
                  onChange={selectedDate => {
                    const date = format(selectedDate, 'yyyy-MM-dd', {
                      locale: ptBR,
                    });
                    setOptionsReport(state => ({
                      ...state,
                      dateStart: date,
                    }));
                    setStartDate(selectedDate);
                  }}
                  mask="__/__/___"
                  placeholder="Dia Inicial"
                />
              </Col>
              <Col xs="6" className="pl-0 pr-2 mb-2 mb-lg-0">
                <DatePicker
                  value={endDate}
                  onChange={selectedDate => {
                    const date = format(selectedDate, 'yyyy-MM-dd', {
                      locale: ptBR,
                    });
                    setOptionsReport(state => ({
                      ...state,
                      dateFinish: date,
                    }));
                    setEndDate(selectedDate);
                  }}
                  mask="__/__/___"
                  placeholder="Dia Final"
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="w-100 mx-0 mb-4 mt-2">
          <Col className="px-0">
            <Label>Qual formato de arquivo que deseja no relatório?</Label>
            <ReactSelect
              name="filetype-general-report"
              id="filetype-general-report"
              options={fileTypes}
              classNamePrefix="select"
              placeholder="Formato de arquivo"
              isClearable
              value={fileTypes.find(p => p?.value === optionsReport?.file_type)}
              onChange={option => {
                setOptionsReport(state => ({
                  ...state,
                  file_type: option?.value,
                }));
              }}
            />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          size="lg"
          color="primary"
          className="new-layout-btn main"
          onClick={handleGenerateReport}
          disabled={exporting}
        >
          Exportar Relatório
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const SplitReceiver = () => {
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { isSupport, isAdmin } = useRoles();
  const roles = useSelector(({ user }) => user.profile.roles);

  const searchQuery = useSelector(({ search }) => search.query);
  const [loading, setLoading] = useState(true);
  const [receivers, setReceivers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGeneralExportModal, setShowGeneralExportModal] = useState(false);
  const [receiverToDelete, setReceiverToDelete] = useState(null);
  const [pagination, setPagination] = useState(
    { current_page: state?.page } || null
  );

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
        'financier',
      ]),
    [roles]
  );

  const params = useMemo(() => {
    return {
      page: pagination?.current_page > 1 ? pagination?.current_page : null,
      query: searchQuery || null,
    };
  }, [pagination, searchQuery]);

  const receiversUrl = useMemo(() => `/${role}/recipients`, [role]);

  const setData = data => {
    setReceivers(
      data.recipients.map(recipient => {
        return {
          ...recipient,
          formatted_created_at: format(
            parseISO(recipient.created_at),
            'dd/MMMM/yyyy',
            {
              locale: ptBR,
            }
          ),
        };
      })
    );
    setPagination(data.meta);
  };

  const fetchReceivers = async (page = null) => {
    try {
      setLoading(true);

      const { data } = await api.get(receiversUrl, {
        params: {
          page:
            page ||
            (pagination?.current_page > 1 ? pagination.current_page : null),
          query: searchQuery || null,
        },
      });

      setReceivers(
        data.recipients.map(recipient => ({
          ...recipient,
          formatted_created_at: format(
            parseISO(recipient.created_at),
            'dd/MMMM/yyyy',
            {
              locale: ptBR,
            }
          ),
        }))
      );
      setPagination(data.meta);
    } catch (err) {
      console.error('Erro ao carregar recebedores', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Recebedores | Viddy';
    fetchReceivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/${role}/recipients/${receiverToDelete.id}`);
      await fetchReceivers();
    } catch (error) {
      console.error('Erro ao excluir recebedor', error);
    }
  };

  useEffect(() => {
    dispatch(resetSearch());
  }, [dispatch]);

  const reportColSize = isSupport ? 2 : 4;

  return (
    <div className="page-content">
      <Container className="container new-layout wide">
        <Row className="w-100 mx-0">
          <Col xs={12} lg={8} className="px-0">
            <PageHeader
              pageTitle="RECEBEDORES"
              responsivePosition="start"
              desktopPosition="start"
            />
          </Col>
          {isSupport && (
            <Col
              xs={12}
              lg={2}
              className="px-0 d-flex align-items-center justify-content-end mb-2 mb-lg-0 pt-2 pt-lg-0"
            >
              <Button
                className="new-layout-btn main d-flex align-items-center justify-content-center"
                color="primary"
                size="lg"
                onClick={() => history.push('/recebedores-split/registrar')}
              >
                Adicionar Recebedor
              </Button>
            </Col>
          )}

          {(isSupport || isAdmin) && (
            <Col
              xs={12}
              lg={reportColSize}
              className="px-0 d-flex align-items-center justify-content-end mb-2 mb-lg-0 pt-2 pt-lg-0"
            >
              <Button
                className="new-layout-btn main d-flex align-items-center justify-content-center"
                color="primary"
                size="lg"
                onClick={() => setShowGeneralExportModal(true)}
              >
                Relatório Conciliação
              </Button>
            </Col>
          )}
        </Row>
        <Row className="w-100 mx-0 mb-2 mb-lg-4 mt-4">
          <Col xs={12} lg={12} className="px-0 mb-4 my-lg-0 d-lg-none">
            <SearchBox
              url={receiversUrl}
              query={searchQuery}
              setQuery={q => dispatch(setSearch({ query: q }))}
              params={params}
              setData={setData}
              setLoading={setLoading}
            />
          </Col>
        </Row>

        {loading ? (
          <Loading />
        ) : (
          <>
            {receivers.map(receiver => (
              <SplitReceiverCard
                key={receiver.id}
                receiver={receiver}
                setReceiverToDelete={setReceiverToDelete}
                setShowDeleteModal={setShowDeleteModal}
              />
            ))}

            {receivers?.length ? (
              <Row className="justify-content-end mb-4 mr-0">
                <Pagination
                  paginationMeta={pagination}
                  url={receiversUrl}
                  setData={setData}
                  setLoading={setLoading}
                  params={params}
                />
              </Row>
            ) : null}

            {!receivers?.length ? (
              <h3 className="mt-3">Nenhum recebedor encontrado.</h3>
            ) : null}
          </>
        )}
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
        <GeneralConciliationModal
          isOpen={showGeneralExportModal}
          onClose={() => setShowGeneralExportModal(false)}
          role={role}
        />
      </Container>
    </div>
  );
};

export default SplitReceiver;
